import DOMPurify from 'dompurify';

/**
 * v-html XSS 방지를 위한 sanitization 유틸리티
 *
 * 사용법:
 * - sanitizeHtml(): 에디터 콘텐츠, 마크다운 렌더링 등 HTML 허용 필요 시
 * - escapeHtml(): 순수 텍스트만 표시할 때 (태그 완전 제거)
 */

// 허용 태그 설정 (기본 서식 + 테이블 + 이미지/링크)
const ALLOWED_TAGS = [
  // 텍스트 서식
  'p',
  'br',
  'b',
  'i',
  'em',
  'strong',
  'u',
  's',
  'del',
  'ins',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'code',
  'pre',
  // 리스트
  'ul',
  'ol',
  'li',
  // 테이블
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'th',
  'td',
  'caption',
  'colgroup',
  'col',
  // 미디어/링크
  'a',
  'img',
  // 구조
  'div',
  'span',
  'hr',
  // 추가 서식
  'sub',
  'sup',
  'mark',
  'small',
  'abbr',
  'cite',
  'dfn',
  'kbd',
  'samp',
  'var',
  'figure',
  'figcaption',
  // SVG
  'svg',
  'path',
  'circle',
  'rect',
  'line',
  'polyline',
  'polygon',
  'ellipse',
  'g',
  'defs',
  'use',
  'symbol',
  'clipPath',
  'mask',
  'pattern',
  'linearGradient',
  'radialGradient',
  'stop',
  'text',
  'tspan',
  'textPath',
  'foreignObject',
  'image',
  'animate',
  'animateTransform',
  'filter',
  'feGaussianBlur',
  'feOffset',
  'feBlend',
  'feFlood',
  'feColorMatrix',
  'feComposite',
  'feMerge',
  'feMergeNode',
  'feImage',
  'feMorphology',
  'feDisplacementMap',
  'feTurbulence',
  'feConvolveMatrix',
  'feDiffuseLighting',
  'feSpecularLighting',
  'fePointLight',
  'feSpotLight',
  'feDistantLight',
  'title',
  'desc',
  'marker',
  'metadata'
];

const ALLOWED_ATTR = [
  // 링크
  'href',
  'target',
  'rel',
  // 이미지
  'src',
  'alt',
  'width',
  'height',
  // 스타일
  'class',
  'style',
  // 테이블
  'colspan',
  'rowspan',
  'scope',
  // 접근성
  'title',
  'lang',
  // 데이터 속성
  'data-*',
  // id (앵커 링크용)
  'id',
  // SVG 속성
  'xmlns',
  'xmlns:xlink',
  'xlink:href',
  'viewBox',
  'preserveAspectRatio',
  'fill',
  'fill-rule',
  'fill-opacity',
  'stroke',
  'stroke-width',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-dasharray',
  'stroke-dashoffset',
  'stroke-opacity',
  'opacity',
  'transform',
  'd',
  'cx',
  'cy',
  'r',
  'rx',
  'ry',
  'x',
  'y',
  'x1',
  'y1',
  'x2',
  'y2',
  'dx',
  'dy',
  'points',
  'clip-path',
  'clip-rule',
  'marker-start',
  'marker-mid',
  'marker-end',
  'gradientUnits',
  'gradientTransform',
  'spreadMethod',
  'offset',
  'stop-color',
  'stop-opacity',
  'patternUnits',
  'patternTransform',
  'patternContentUnits',
  'maskUnits',
  'maskContentUnits',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'text-anchor',
  'text-decoration',
  'dominant-baseline',
  'alignment-baseline',
  'letter-spacing',
  'word-spacing',
  'color',
  'overflow',
  'visibility',
  'display',
  'filter',
  'flood-color',
  'flood-opacity',
  'lighting-color',
  'color-interpolation-filters',
  'stdDeviation',
  'in',
  'in2',
  'result',
  'mode',
  'type',
  'values',
  'k1',
  'k2',
  'k3',
  'k4',
  'operator',
  'surfaceScale',
  'diffuseConstant',
  'specularConstant',
  'specularExponent',
  'azimuth',
  'elevation',
  'baseFrequency',
  'numOctaves',
  'seed',
  'stitchTiles',
  'refX',
  'refY',
  'markerWidth',
  'markerHeight',
  'orient',
  'markerUnits'
];

/**
 * HTML 콘텐츠를 안전하게 sanitize
 * 에디터(TipTap, Vue2-Editor) 출력, 마크다운 렌더링 결과 등에 사용
 *
 * @param {string} html - sanitize할 HTML 문자열
 * @returns {string} - 안전한 HTML 문자열
 */
export function sanitizeHtml(html) {
  if (!html) return '';
  if (typeof html !== 'string') return '';

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    KEEP_CONTENT: true,
    ALLOW_DATA_ATTR: true,
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover']
  });
}

/**
 * 텍스트를 HTML 엔티티로 이스케이프
 * 순수 텍스트만 표시할 때 사용 (모든 HTML 태그 무효화)
 *
 * @param {string} text - 이스케이프할 텍스트
 * @returns {string} - 이스케이프된 문자열
 */
export function escapeHtml(text) {
  if (!text) return '';
  if (typeof text !== 'string') return String(text);

  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * 텍스트를 이스케이프하고 개행을 <br>로 변환
 * 테이블 셀 등 개행만 허용할 때 사용
 *
 * @param {string} text - 변환할 텍스트
 * @returns {string} - 안전하게 변환된 HTML 문자열
 */
export function formattedTextSafe(text) {
  if (typeof text !== 'string' || !text) return text;
  return escapeHtml(text).replace(/\n/g, '<br>');
}

/**
 * HTML 콘텐츠 내 검색어를 <mark>로 하이라이트
 * sanitizeHtml과 함께 사용 가능 (mark 태그는 ALLOWED_TAGS에 포함)
 *
 * @param {string} html - 원본 HTML 문자열
 * @param {string} query - 하이라이트할 검색어
 * @returns {string} - 하이라이트가 적용된 HTML 문자열
 */
export function highlightSearch(html, query) {
  if (!html || !query) return html || '';
  if (typeof html !== 'string') return '';

  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');

  // HTML 태그 내부가 아닌 텍스트 노드에서만 치환
  return html.replace(/(<[^>]*>)|([^<]+)/g, (match, tag, text) => {
    if (tag) return tag;
    return text.replace(regex, '<mark class="search-highlight">$1</mark>');
  });
}

/**
 * 순수 텍스트에서 검색어를 하이라이트 (XSS 방지 후 mark 적용)
 *
 * @param {string} text - 원본 텍스트
 * @param {string} query - 하이라이트할 검색어
 * @returns {string} - 이스케이프 + 하이라이트된 HTML 문자열
 */
export function highlightText(text, query) {
  if (!text || !query) return escapeHtml(text);
  if (typeof text !== 'string') return '';

  const safe = escapeHtml(text);
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');
  return safe.replace(regex, '<mark class="search-highlight">$1</mark>');
}

/**
 * \r\n / \r 줄바꿈을 \n으로 정규화
 *
 * @param {string} text - 정규화할 텍스트
 * @returns {string} - \n으로 통일된 문자열
 */
export function normalizeNewlines(text) {
  if (!text) return '';
  return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}
